import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, ExternalLink, AlertCircle, Video } from "lucide-react";
import DOMPurify from "dompurify";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";
const HEADER_BASE_HEIGHT = 75;
const MODO_DESARROLLO = process.env.REACT_APP_MODO_DESARROLLO === "true";
const DEV_BANNER_HEIGHT = 40;
const FALLBACK_RESOURCE_IMAGE = "/placeholder.png";
const IMAGE_RETRY_DELAY_MS = 300;

function isDataImageUrl(value) {
  return typeof value === "string" && value.startsWith("data:image/");
}

async function fetchResourceImageWithRetry(recursoId) {
  const fetchOnce = async () => {
    const imgRes = await fetch(`${API_BASE}/recursos/${recursoId}/imagen`);
    if (!imgRes.ok) throw new Error("image_fetch_failed");
    const txt = await imgRes.text();
    if (!isDataImageUrl(txt)) throw new Error("invalid_image_payload");
    return txt;
  };

  try {
    return await fetchOnce();
  } catch {
    try {
      await new Promise((resolve) => setTimeout(resolve, IMAGE_RETRY_DELAY_MS));
      return await fetchOnce();
    } catch {
      return FALLBACK_RESOURCE_IMAGE;
    }
  }
}

function getAuthValue(key) {
  const ls = localStorage.getItem(key);
  if (ls !== null && ls !== "undefined") return ls;
  const ss = sessionStorage.getItem(key);
  return ss !== "undefined" ? ss : null;
}

function buildRecursoGoUrl(apiBase, recursoId) {
  const base = String(apiBase || "/api").replace(/\/$/, "");
  const href = `${base}/recursos/${recursoId}/go`;
  const token = getAuthValue("token");
  if (!token) return href;
  return `${href}?token=${encodeURIComponent(token)}`;
}

function hasTutorialUrl(recurso) {
  return !!String(recurso?.urlTutorial || "").trim();
}

export default function RecursoPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [recurso, setRecurso] = useState(null);
  const [imagen, setImagen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [abriendo, setAbriendo] = useState(false);

  const offsetTop =
    HEADER_BASE_HEIGHT + (MODO_DESARROLLO ? DEV_BANNER_HEIGHT : 0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const esInvitado = getAuthValue("guest") === "true";
        const esAnonimo = getAuthValue("anonimo") === "true";
        const modoLimitado = esInvitado || esAnonimo;

        const recursosUrl = modoLimitado
          ? `${API_BASE}/recursos?anon=true`
          : `${API_BASE}/recursos`;

        const recRes = await fetch(recursosUrl);
        const recursosData = await recRes.json();

        // Buscar el recurso específico
        const recursoEncontrado = recursosData.find((r) => r.id === parseInt(id));

        if (!recursoEncontrado) {
          setError("Recurso no encontrado");
          return;
        }

        if (recursoEncontrado.estado === "INACTIVO") {
          setError("Este recurso no está disponible");
          return;
        }

        setRecurso(recursoEncontrado);

        // Cargar imagen
        const imgSrc = await fetchResourceImageWithRetry(recursoEncontrado.id);
        setImagen(imgSrc);
      } catch (err) {
        console.error("Error cargando recurso:", err);
        setError("Error al cargar el recurso");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleAbrirRecurso = async () => {
    if (!recurso) return;
    setAbriendo(true);

    try {
      const url = buildRecursoGoUrl(API_BASE, recurso.id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Error abriendo recurso:", err);
      setError("Error al intentar acceder al recurso");
    } finally {
      setAbriendo(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{ paddingTop: offsetTop }}
    >
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4"
          >
            <ChevronLeft className="w-5 h-5" />
            Volver atrás
          </button>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-500">Cargando recurso...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex gap-4">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-semibold text-red-900">{error}</h2>
              <button
                onClick={() => navigate(-1)}
                className="mt-2 text-sm text-red-700 hover:text-red-900 underline"
              >
                Volver a la categoría
              </button>
            </div>
          </div>
        ) : recurso ? (
          <article className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Imagen */}
            {imagen && (
              <div className="h-80 md:h-96 bg-gray-100 overflow-hidden">
                <img
                  src={imagen}
                  alt={recurso.nombre}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK_RESOURCE_IMAGE;
                  }}
                />
              </div>
            )}

            {/* Contenido */}
            <div className="p-6 md:p-8">
              {/* Categoría y áreas */}
              <div className="flex flex-wrap gap-2 mb-4">
                {recurso.categoria && (
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
                    {recurso.categoria.nombre}
                  </span>
                )}
                {recurso.areas?.map((area) => (
                  <span
                    key={area.id}
                    className="inline-block px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-full"
                  >
                    {area.nombre}
                  </span>
                ))}
              </div>

              {/* Título */}
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {recurso.nombre}
              </h1>

              {/* Fecha */}
              {recurso.fecha && (
                <p className="text-sm text-gray-500 mb-6">
                  Publicado: {new Date(recurso.fecha).toLocaleDateString("es-ES", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              )}

              {/* Descripción */}
              <div className="prose max-w-none mb-8">
                <div
                  className="text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(recurso.descripcion || ""),
                  }}
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleAbrirRecurso}
                  disabled={abriendo}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ExternalLink className="w-5 h-5" />
                  {abriendo ? "Abriendo..." : "Abrir recurso"}
                </button>

                {hasTutorialUrl(recurso) && (
                  <a
                    href={String(recurso.urlTutorial).trim()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Video className="w-5 h-5" />
                    Ver tutorial
                  </a>
                )}
              </div>
            </div>
          </article>
        ) : null}
      </div>
    </div>
  );
}
